import { AfterViewInit, Component, ElementRef, HostListener, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { titleSplit } from 'src/app/utils/title-split';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';


// declare var Vimeo: any; 

@Component({
  selector: 'app-video-modal',
  templateUrl: './video-modal.component.html',
  styleUrls: ['./video-modal.component.scss']
})
export class VideoModalComponent implements OnInit, AfterViewInit {
  @ViewChild('videoPlayer') videoPlayer!: ElementRef<HTMLVideoElement>;

  availableQualities: string[] = ['Auto', '480p', '720p', '1080p', '1920p (Full HD)'];
  selectedQuality: string = 'Auto';
  showQualityMenu: boolean = false;

  constructor(@Inject(MAT_DIALOG_DATA) public data: any, private ref: MatDialogRef<VideoModalComponent>, private elementRef: ElementRef, private sanitizer: DomSanitizer) { }

  @HostListener('document:click', ['$event'])
  onDocumentClick(): void {
    this.showQualityMenu = false;
    if (this.videoPlayer?.nativeElement) {
      const video = this.videoPlayer.nativeElement;
      if (video.muted && !video.paused) {
        video.muted = false;
      }
    }
  }

  toggleQualityMenu(event: Event): void {
    event.stopPropagation();
    this.showQualityMenu = !this.showQualityMenu;
  }

  getQualityClass(): string {
    switch (this.selectedQuality) {
      case '480p':
        return 'quality-480p';
      case '720p':
        return 'quality-720p';
      case '1080p':
        return 'quality-1080p';
      case '1920p (Full HD)':
        return 'quality-1920p';
      default:
        return 'quality-Auto';
    }
  }

  changeQuality(quality: string): void {
    if (this.selectedQuality === quality) {
      this.showQualityMenu = false;
      return;
    }

    const player = this.videoPlayer?.nativeElement;
    const currentTime = player ? player.currentTime : 0;
    const isPaused = player ? player.paused : true;

    this.selectedQuality = quality;
    this.showQualityMenu = false;

    if (player) {
      const targetSrc = this.getVideoSource();

      if (player.src !== targetSrc) {
        player.pause();

        const onLoadedMetadata = () => {
          player.removeEventListener('loadedmetadata', onLoadedMetadata);
          try {
            if (currentTime > 0 && !isNaN(player.duration) && currentTime < player.duration) {
              player.currentTime = currentTime;
            }
          } catch (err) {
            console.warn('Unable to seek video to currentTime:', err);
          }
          if (!isPaused) {
            player.play().catch(err => console.warn('Quality change playback error:', err));
          }
        };

        const onError = () => {
          player.removeEventListener('error', onError);
          player.removeEventListener('loadedmetadata', onLoadedMetadata);
          console.warn('Resolution variant URL not found on server, applying quality mode to base stream.');
          const fallbackSrc = this.getVideoSource(true);
          if (player.src !== fallbackSrc) {
            player.src = fallbackSrc;
            player.load();
            if (!isPaused) {
              player.play().catch(e => console.warn('Fallback play error:', e));
            }
          }
        };

        player.addEventListener('loadedmetadata', onLoadedMetadata);
        player.addEventListener('error', onError, { once: true });

        player.src = targetSrc;
        player.load();
      }
    }
  }

  getVideoSource(forceBase: boolean = false): string {
    if (!this.data) return '';
    const baseVideo = this.data.video || '';

    if (forceBase || this.selectedQuality === 'Auto' || !baseVideo) {
      return baseVideo;
    }

    if (this.data.qualities && this.data.qualities[this.selectedQuality]) {
      return this.data.qualities[this.selectedQuality];
    }

    const qualityKeyMap: { [key: string]: string[] } = {
      '480p': ['video_480p', 'video_480', 'url_480p'],
      '720p': ['video_720p', 'video_720', 'url_720p'],
      '1080p': ['video_1080p', 'video_1080', 'url_1080p'],
      '1920p (Full HD)': ['video_1920p', 'video_1920', 'video_1080p', 'url_1920p']
    };

    const keys = qualityKeyMap[this.selectedQuality] || [];
    for (const key of keys) {
      if (this.data[key]) {
        return this.data[key];
      }
    }

    const suffixMap: { [key: string]: string } = {
      '480p': '_480p',
      '720p': '_720p',
      '1080p': '_1080p',
      '1920p (Full HD)': '_1920p'
    };

    const suffix = suffixMap[this.selectedQuality];
    if (suffix && baseVideo.includes('.mp4') && !baseVideo.includes('X-Amz-Signature') && !baseVideo.includes('Signature=')) {
      const parts = baseVideo.split('.mp4');
      return `${parts[0]}${suffix}.mp4${parts.slice(1).join('.mp4')}`;
    }

    return baseVideo;
  }

  getSafeVideoUrl(): SafeResourceUrl | string {
    let url = this.data.video || '';
    if (this.data.youtube && url && !url.includes('autoplay=')) {
      url += (url.includes('?') ? '&' : '?') + 'autoplay=1';
    }
    if (this.data.vimeo && url && !url.includes('autoplay=')) {
      url += (url.includes('?') ? '&' : '?') + 'autoplay=1';
    }
    if (this.data.vimeo || this.data.youtube) {
      return this.sanitizer.bypassSecurityTrustResourceUrl(url);
    }
    return '';
  }

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    if (this.videoPlayer?.nativeElement) {
      const video = this.videoPlayer.nativeElement;
      video.muted = false;
      video.load();
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch(err => {
          console.warn('Unmuted autoplay restricted by browser, playing muted until user gesture:', err);
          video.muted = true;
          video.play().catch(e => console.warn('Muted play error:', e));
        });
      }
    }
  }

  gettitle(title: string | null, part = false): string {
    return titleSplit(title, part);
  }

  closeDialog() {
    this.ref.close();
  }

  skipBackward(seconds: number = 10): void {
    if (this.videoPlayer && this.videoPlayer.nativeElement) {
      const player = this.videoPlayer.nativeElement;
      player.currentTime = Math.max(0, player.currentTime - seconds);
    }
  }

  skipForward(seconds: number = 10): void {
    if (this.videoPlayer && this.videoPlayer.nativeElement) {
      const player = this.videoPlayer.nativeElement;
      const targetTime = player.currentTime + seconds;
      player.currentTime = isNaN(player.duration) ? targetTime : Math.min(player.duration, targetTime);
    }
  }
}